/**
 * One app's connection on a Connect card: its setup (OAuth or a credential
 * form), the account's connection and the actions a row offers.
 */
import { useEffect, useRef, useState } from "react";
import {
  connectWithCredentials,
  disconnectToolkit,
  getConnectionStatus,
  getToolkitConnectionSetup,
  IntegrationsApiError,
  listConnections,
  type ConnectionCredentialValue,
  type IntegrationConnection,
  type ToolkitConnectionSetup,
} from "@/api/integrations";
import { useIntegrationStore } from "@/stores/integration-store";

export type SetupError =
  | "credentialsRejected"
  | "checkKeyFields"
  | "connectFailed"
  | "checkFailed"
  | "cancelPreviousFailed";

export function useConnectionSetup(toolkitSlug: string) {
  const [setup, setSetup] = useState<ToolkitConnectionSetup | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingAgain, setEditingAgain] = useState(false);
  const [cancelledConnectionId, setCancelledConnectionId] = useState<
    string | null
  >(null);
  const [error, setError] = useState<SetupError | null>(null);
  const [submitted, setSubmitted] = useState<IntegrationConnection | null>(
    null,
  );
  const mounted = useRef(true);
  const storedConnection = useIntegrationStore((state) =>
    state.connections.get(toolkitSlug),
  );
  const connection =
    storedConnection?.id === cancelledConnectionId
      ? undefined
      : storedConnection;
  const pending = useIntegrationStore((state) =>
    state.pendingAuth.has(toolkitSlug),
  );
  const actions = useIntegrationStore((state) => state.actions);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!toolkitSlug) return;
    void getToolkitConnectionSetup(toolkitSlug)
      .then((result) => {
        if (!cancelled) setSetup(result);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [toolkitSlug, loadAttempt]);

  const current = submitted ?? connection;
  const apiKey = setup?.auth_scheme === "API_KEY" && setup.fields.length > 0;

  async function acceptConnection(result: IntegrationConnection) {
    if (!mounted.current) return;
    setSubmitted(result);
    setEditingAgain(false);
    if (result.status !== "active" && result.status !== "initiated") {
      setError("credentialsRejected");
    }
    try {
      // Another window may have replaced this account while the request was
      // finishing. Only a successful server read may supersede its result.
      const latest = (await listConnections()).find(
        (row) => row.toolkit_slug === toolkitSlug,
      );
      if (!mounted.current) return;
      useIntegrationStore.setState((state) => {
        const connections = new Map(state.connections);
        if (latest) connections.set(toolkitSlug, latest);
        else connections.delete(toolkitSlug);
        return { connections };
      });
      setSubmitted(null);
    } catch {
      // Keep the confirmed submission result when reconciliation is unavailable.
    }
  }

  async function run(
    work: () => Promise<void>,
    failure: (e: unknown) => SetupError,
  ) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (caught) {
      if (mounted.current) setError(failure(caught));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  return {
    setup,
    loadError,
    busy,
    error,
    apiKey,
    active: current?.status === "active",
    pending:
      pending || (!!setup && !apiKey && connection?.status === "initiated"),
    awaitingCredentials:
      apiKey && !editingAgain && current?.status === "initiated",
    retryLoad() {
      setLoadError(false);
      setLoadAttempt((value) => value + 1);
    },
    connect: () => void actions.initiateConnection(toolkitSlug),
    cancel: () => void actions.disconnect(toolkitSlug),
    submit: (fields: Record<string, ConnectionCredentialValue>) =>
      void run(
        async () =>
          acceptConnection(await connectWithCredentials(toolkitSlug, fields)),
        // Never render upstream exception text: providers can echo credential data.
        (failure) =>
          failure instanceof IntegrationsApiError && failure.status === 400
            ? "checkKeyFields"
            : "connectFailed",
      ),
    checkStatus: () =>
      void run(
        async () => acceptConnection(await getConnectionStatus(toolkitSlug)),
        () => "checkFailed",
      ),
    replaceCredentials: () =>
      void run(
        async () => {
          // The store's disconnect action swallows failures. Await the endpoint so
          // replacement credentials cannot silently attach to a previous attempt.
          await disconnectToolkit(toolkitSlug);
          if (!mounted.current) return;
          setCancelledConnectionId(current?.id ?? null);
          setSubmitted(null);
          setEditingAgain(true);
          await actions.refreshConnections();
        },
        () => "cancelPreviousFailed",
      ),
  };
}
